import React, { Suspense, useEffect, useRef } from 'react';
import { Route, Switch, useParams } from 'react-router';
import classNames from 'classnames/bind';
import { useSelector } from 'react-redux';
import { Howler } from 'howler';
import { games } from '../../games/config';
import PageContainer from '../PageContainer/PageContainer';
import GamesList from '../../components/GamesList/GamesList';
import styles from './styles.scss';
import Restriction from '../../static/Restriction';
import AreYouAdult from '../../components/Popup/AreYouAdult';
import Maintenance from '../../static/Maintenance';
import StatsTable from '../../components/StatsTable/StatsTable';
import GameCodeContext from './GameCodeContext';
import useAppStore from '../../useAppStore';
import { errorToast } from '../../helpers/toast';

const cx = classNames.bind(styles);

export default function Games() {
  const { gameUrl } = useParams();
  const prevSoundState = useRef(Howler._muted);
  const accessAvailable = useSelector(state => state.info?.accessAvailable);
  const maintenance = useSelector(state => state.info?.maintenance);
  const selectedAsset = useAppStore(state => state.asset.selected);

  const gameCode = games[gameUrl]
    ? gameUrl
    : Object.entries(games).find(([, { urlName }]) => urlName && urlName === gameUrl)?.[0];

  useEffect(() => {
    function visibilityListener() {
      const { hidden } = document;
      if (!hidden) {
        Howler.mute(prevSoundState.current);
      } else {
        prevSoundState.current = Howler._muted;
        Howler.mute(true);
      }
    }
    document.addEventListener('visibilitychange', visibilityListener);
    return () => document.removeEventListener('visibilitychange', visibilityListener);
  }, []);

  useEffect(() => {
    if (games[gameCode]?.pgpDisabled) {
      if (selectedAsset === 'PGP') {
        useAppStore.getState().setSelectedAsset('BSV');
      }
      const unsubscribe = useAppStore.subscribe(
        selected => {
          if (games[gameCode].pgpDisabled && selected === 'PGP') {
            useAppStore.getState().setSelectedAsset('BSV');
            errorToast(`${games[gameCode].label} does not support PGP payments`);
          }
        },
        state => state.asset.selected
      );
      return () => {
        unsubscribe();
        if (!games[gameCode].pgpDisabled) {
          useAppStore.getState().setSelectedAsset(selectedAsset);
        }
      };
    }
  }, [gameCode]);

  if (!accessAvailable) {
    return <Restriction />;
  }

  if (maintenance?.code === '0') {
    const isMaintenance = maintenance.game.find(({ name }) => name === gameCode)?.is_maintenance === 'Y';
    if (isMaintenance) {
      return <Maintenance site={false} />;
    }
  }

  return (
    <>
      <AreYouAdult />
      <Switch>
        {/* eslint-disable-next-line no-shadow */}
        {Object.entries(games).map(([gameCode, { urlName, label, component: Component }]) => (
          <Route path={`/games/${urlName || gameCode}`}>
            <GameCodeContext.Provider value={gameCode}>
              <PageContainer showFooter showHeader dark pageTitle={label} contentClass={cx('game-page')}>
                <Suspense
                  fallback={(
                    <div className={cx('game-loader')}>
                      <span className={cx('bold', 'large')}>Loading game...</span>
                    </div>
                  )}
                >
                  <Component />
                </Suspense>
                {!['bitto', 'cfBattle', 'slots'].includes(gameCode) && <StatsTable />}
              </PageContainer>
            </GameCodeContext.Provider>
          </Route>
        ))}
        <Route path="/games">
          <PageContainer showFooter showHeader dark pageTitle="Games" contentClass={cx('games-page')}>
            <GamesList />
          </PageContainer>
        </Route>
      </Switch>
    </>
  );
}
